import React, { useState } from 'react';
import { Camera, Book, Heart, Play, Pause, Video, Mic, Upload, Users, Home, Settings, Star, Cloud, Moon, Sun, ChevronRight, Plus, Check, X, Mail, Lock, User, ArrowRight, Sparkles } from 'lucide-react';

const Button = ({ children, onClick, variant = 'primary', style = {} }) => {
  const baseStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.75rem',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    ...style
  };

  const variants = {
    primary: {
      background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
      color: 'white',
    },
    secondary: {
      background: 'linear-gradient(135deg, #db2777 0%, #f97316 100%)',
      color: 'white',
    },
    blue: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    outline: {
      background: 'transparent',
      color: '#9333ea',
      border: '2px solid #9333ea',
    }
  };

  return (
    <button onClick={onClick} style={{ ...baseStyle, ...variants[variant] }}>
      {children}
    </button>
  );
};

const Card = ({ children, style = {}, hover = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '1.5rem',
        boxShadow: isHovered && hover ? '0 20px 50px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.07)',
        transition: 'all 0.3s ease',
        transform: isHovered && hover ? 'translateY(-8px)' : 'translateY(0)',
        ...style
      }}
      onMouseEnter={() => hover && setIsHovered(true)}
      onMouseLeave={() => hover && setIsHovered(false)}
    >
      {children}
    </div>
  );
};

export const LandingPage = () => {
  const handleSelectUserType = (type) => {
    sessionStorage.setItem('selectedUserType', type);
    window.location.href = '/login';
  };

  const handleNavigateToSignup = () => {
    window.location.href = '/signup';
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #fce7f3 50%, #dbeafe 100%)' }}>
      {/* Hero Section */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '56rem', margin: '0 auto 3rem' }}>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            background: 'rgba(147, 51, 234, 0.1)', 
            padding: '0.5rem 1rem', 
            borderRadius: '9999px', 
            marginBottom: '1.5rem',
            fontSize: '0.875rem'
          }}>
            <Sparkles style={{ width: '1rem', height: '1rem', color: '#9333ea', flexShrink: 0 }} />
            <span style={{ color: '#9333ea', fontWeight: '600' }}>Connecting Generations Through Stories</span>
          </div>
          <span style={{ 
            fontSize: '2.5rem',
            fontWeight: '900',
            justifyContent: 'center',
            fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", cursive, sans-serif',
            display: 'flex', 
            gap: '0.1rem',
            letterSpacing: '0.05rem'
          }}>
            <span style={{ 
              color: '#ff4757',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>R</span>
            <span style={{ 
              color: '#ff6b35',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>e</span>
            <span style={{ 
              color: '#ffd93d',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>a</span>
            <span style={{ 
              color: '#a855f7',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>d</span>
            <span style={{ 
              color: '#ffd93d',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>2</span>
            <span style={{ 
              color: '#06b6d4',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>M</span>
            <span style={{ 
              color: '#3b82f6',
              WebkitTextStroke: '3px black',
              paintOrder: 'stroke fill',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
            }}>e</span>
          </span>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 8vw, 4rem)',
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            background: 'linear-gradient(90deg, #9333ea 0%, #db2777 50%, #2563eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.1',
            padding: '0 1rem'
          }}>
            Bedtime Stories in Grandma's Voice
          </h1>
          
          <p style={{ 
            fontSize: 'clamp(1.125rem, 4vw, 1.5rem)', 
            color: '#374151', 
            marginBottom: '1rem', 
            lineHeight: '1.6', 
            fontWeight: '500',
            padding: '0 1rem'
          }}>
            Even when she's miles away
          </p>
          
          <p style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.125rem)', 
            color: '#6b7280', 
            marginBottom: '2rem', 
            lineHeight: '1.7', 
            maxWidth: '42rem', 
            margin: '0 auto 2rem',
            padding: '0 1rem'
          }}>
            Record personalized storybooks that bring family closer, one bedtime at a time. No robots, no strangers—just the voices your children love most.
          </p>
          
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center', 
            flexWrap: 'wrap', 
            marginBottom: '2rem',
            padding: '0 1rem'
          }}>
            <Button 
              onClick={() => handleSelectUserType('parent')}
              variant="primary"
              style={{ 
                padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(1.5rem, 4vw, 2.5rem)', 
                fontSize: 'clamp(0.95rem, 2vw, 1.125rem)',
                width: window.innerWidth < 640 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              I'm a Parent <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
            </Button>
            <Button 
              onClick={() => handleSelectUserType('grandparent')}
              variant="secondary"
              style={{ 
                padding: 'clamp(0.875rem, 2vw, 1.25rem) clamp(1.5rem, 4vw, 2.5rem)', 
                fontSize: 'clamp(0.95rem, 2vw, 1.125rem)',
                width: window.innerWidth < 640 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              I'm a Grandparent/Reader <Heart style={{ width: '1.25rem', height: '1.25rem' }} />
            </Button>
          </div>

          <button 
            onClick={handleNavigateToSignup}
            style={{
              background: 'none',
              border: 'none',
              color: '#9333ea',
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '0.5rem'
            }}
          >
            Don't have an account? Sign up
          </button>
        </div>

        {/* How It Works Section */}
        <div style={{ marginBottom: '4rem', marginTop: '4rem' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', 
            fontWeight: 'bold', 
            textAlign: 'center', 
            marginBottom: '2.5rem',
            color: '#1f2937',
            padding: '0 1rem'
          }}>
            How It Works
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : window.innerWidth < 1024 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '2rem', 
            maxWidth: '80rem', 
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            <Card style={{ padding: '2rem', position: 'relative' }}>
              <div style={{ 
                position: 'absolute', 
                top: '-1rem', 
                left: '2rem',
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)'
              }}>
                1
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <Users style={{ width: '2.5rem', height: '2.5rem', color: '#9333ea', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.375rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#1f2937' }}>
                  Parents Create a Family
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6', fontSize: 'clamp(0.95rem, 2vw, 1rem)' }}>
                  Set up your family account, add your children, and invite grandparents or trusted family members to join.
                </p>
              </div>
            </Card>

            <Card style={{ padding: '2rem', position: 'relative' }}>
              <div style={{ 
                position: 'absolute', 
                top: '-1rem', 
                left: '2rem',
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #db2777 0%, #f97316 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(219, 39, 119, 0.3)'
              }}>
                2
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <Book style={{ width: '2.5rem', height: '2.5rem', color: '#db2777', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.375rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#1f2937' }}>
                  Assign Books to Read
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6', fontSize: 'clamp(0.95rem, 2vw, 1rem)' }}>
                  Choose which books you'd like recorded for each child. Grandparents see exactly what to read.
                </p>
              </div>
            </Card>

            <Card style={{ padding: '2rem', position: 'relative' }}>
              <div style={{ 
                position: 'absolute', 
                top: '-1rem', 
                left: '2rem',
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
              }}>
                3
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <Video style={{ width: '2.5rem', height: '2.5rem', color: '#2563eb', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.375rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#1f2937' }}>
                  Grandparents/Reader Record
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6', fontSize: 'clamp(0.95rem, 2vw, 1rem)' }}>
                  Simple one-click recording. Video to capture their smile, or audio-only for convenience.
                </p>
              </div>
            </Card>

            <Card style={{ padding: '2rem', position: 'relative', gridColumn: 'span 1' }}>
              <div style={{ 
                position: 'absolute', 
                top: '-1rem', 
                left: '2rem',
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}>
                4
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <Moon style={{ width: '2.5rem', height: '2.5rem', color: '#10b981', marginBottom: '1rem' }} />
                <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.375rem)', fontWeight: 'bold', marginBottom: '0.75rem', color: '#1f2937' }}>
                  Enjoy Story Time
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6', fontSize: 'clamp(0.95rem, 2vw, 1rem)' }}>
                  Access all recordings anytime. Use bedtime mode to play through all of your child's stories automatically.
                </p>
              </div>
            </Card>
          </div>
        </div>

        {/* Features Section */}
        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', 
            fontWeight: 'bold', 
            textAlign: 'center', 
            marginBottom: '2.5rem',
            color: '#1f2937',
            padding: '0 1rem'
          }}>
            Why Families Love Us
          </h2>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            padding: '0 1rem'
          }}>
            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#f3e8ff', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Heart style={{ width: '2rem', height: '2rem', color: '#9333ea' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>No Robots Allowed</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Your children deserve the warmth of real voices from people who love them, not generic AI narration.
              </p>
            </Card>

            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#fce7f3', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Users style={{ width: '2rem', height: '2rem', color: '#db2777' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>Private & Secure</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Family-only access. Your recordings are private and only shared with people you invite.
              </p>
            </Card>

            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#dbeafe', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Sparkles style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>Grandparent-Friendly</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Simple, intuitive interface designed for all ages. If they can video call, they can record stories.
              </p>
            </Card>

            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#fef3c7', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Moon style={{ width: '2rem', height: '2rem', color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>Bedtime Mode</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Automatically play through all your child's recordings for a seamless bedtime routine.
              </p>
            </Card>

            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#dcfce7', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Book style={{ width: '2rem', height: '2rem', color: '#10b981' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>Growing Library</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Build a collection of recordings that becomes a treasured family heirloom over time.
              </p>
            </Card>

            <Card style={{ padding: '2rem' }} hover>
              <div style={{ width: '4rem', height: '4rem', background: '#f3e8ff', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <Video style={{ width: '2rem', height: '2rem', color: '#7c3aed' }} />
              </div>
              <h3 style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>Video or Audio</h3>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)', color: '#4b5563', lineHeight: '1.6' }}>
                Record with video to capture expressions and gestures, or audio-only for simplicity.
              </p>
            </Card>
          </div>
        </div>

        {/* Testimonial Section */}
        <div style={{ 
          background: 'white', 
          borderRadius: '2rem', 
          padding: 'clamp(2rem, 5vw, 4rem) clamp(1rem, 3vw, 2rem)',
          marginBottom: '4rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{ maxWidth: '48rem', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ marginBottom: '2rem' }}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} style={{ width: 'clamp(1.5rem, 4vw, 2rem)', height: 'clamp(1.5rem, 4vw, 2rem)', color: '#fbbf24', fill: '#fbbf24', display: 'inline-block' }} />
              ))}
            </div>
            <p style={{ fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', color: '#374151', lineHeight: '1.8', marginBottom: '2rem', fontStyle: 'italic' }}>
              "My daughter asks for 'Nana's stories' every night. Even though my mom lives across the country, she's part of our bedtime routine now. It's magical."
            </p>
            <p style={{ color: '#9333ea', fontWeight: '600', fontSize: 'clamp(1rem, 2.5vw, 1.125rem)' }}>
              - Sarah M. - Parent of Two
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '1.5rem',
            color: '#1f2937'
          }}>
            Ready to Bring Your Family Closer?
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)', color: '#6b7280', marginBottom: '2rem', maxWidth: '36rem', margin: '0 auto 2rem' }}>
            Start creating memories that will last a lifetime.
          </p>
          <Button 
            onClick={handleNavigateToSignup}
            variant="primary"
            style={{ 
              padding: 'clamp(1rem, 2vw, 1.25rem) clamp(2rem, 5vw, 3rem)', 
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              width: window.innerWidth < 640 ? 'calc(100% - 2rem)' : 'auto',
              maxWidth: '100%'
            }}
          >
            Get Started Free <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
          </Button>
        </div>
      </div>
    </div>
  );
};